@if (isFixed() && (isOpen() || hasOpened())) {
  <div
    class="song-drawer-progressive-blur fixed top-16 inset-x-0 bottom-0 z-90 transition-opacity duration-300 ease-in-out cursor-pointer"
    [class.is-active]="isOpen()"
    [class.opacity-100]="isOpen()"
    [class.opacity-0]="!isOpen()"
    [class.pointer-events-none]="!isOpen()"
    (click)="closeSidebar()"
    aria-hidden="true"
  >
    <div class="blur-slice blur-slice-1"></div>
    <div class="blur-slice blur-slice-2"></div>
    <div class="blur-slice blur-slice-3"></div>
    <div class="blur-slice blur-slice-4"></div>
    <div class="blur-slice blur-slice-5"></div>
    <div class="blur-slice blur-slice-6"></div>
    <div class="blur-tint"></div>
  </div>
}

<aside
  class="flex flex-col bg-side-drawer-bg"
  [ngClass]="asideNgClass()"
>
  @if (selectedSongId() || songDetails()) {
    <div
      class="relative flex h-full min-h-0 flex-1 flex-col bg-side-drawer-bg text-text-primary"
    >
      <!-- Top Bar: Status / Role + Close Button (12px padding) -->
      <div class="flex items-center justify-between px-3 pt-3 pb-1 shrink-0">
        <span class="text-xs font-medium text-text-dimmed">Song</span>
        <button
          type="button"
          (click)="closeSidebar()"
          class="rounded-full p-1.5 text-text-dimmed hover:text-text-primary hover:bg-white/10 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Scrollable Main Content (12px padding) -->
      <div id="song-profile-preview-drawer-scroll" class="custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-clip px-3 py-2">
        <div class="flex flex-col gap-4">
          
          <!-- Header Info -->
          @if (loading()) {
            <div class="flex items-center gap-3.5 animate-pulse">
              <div class="h-[110px] w-[110px] shrink-0 rounded-2xl bg-card-bg-hover"></div>
              <div class="flex min-w-0 flex-1 flex-col gap-2">
                <div class="h-6 w-3/4 bg-card-bg-hover rounded"></div>
                <div class="h-4 w-1/2 bg-card-bg-hover rounded"></div>
                <div class="h-5 w-16 bg-card-bg-hover rounded-full"></div>
              </div>
            </div>

            <!-- Skeleton Stats Grid -->
            <div class="grid grid-cols-2 gap-2">
              @for (i of [1, 2, 3, 4]; track i) {
                <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
                  <div class="h-6 w-16 bg-card-bg-hover animate-pulse rounded mb-1"></div>
                  <div class="h-3 w-20 bg-card-bg-hover animate-pulse rounded"></div>
                </div>
              }
            </div>
          } @else if (songDetails(); as song) {
            <!-- Song Header (110px Rounded Avatar + Info) -->
            <div class="flex items-center gap-3.5">
              <img
                [src]="song.imageUrl || songImageFallback(song.title)"
                (error)="onDrawerImageError($event, song.title)"
                class="h-[110px] w-[110px] shrink-0 rounded-2xl border border-white/10 bg-zinc-800 object-cover shadow-lg"
                [alt]="song.title"
              />
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <h3
                  class="font-bold leading-tight tracking-tight text-text-primary text-2xl truncate"
                  [title]="song.title"
                >
                  {{ song.title }}
                </h3>
                <p class="text-xs text-text-half-dimmed font-normal truncate">
                  by {{ getArtistsList(song.artists) }}
                </p>
                @if (song.explicit) {
                  <div class="flex flex-wrap items-center gap-1.5 mt-1">
                    <app-badge variant="neutral" size="tiny" label="Explicit" />
                  </div>
                }
              </div>
            </div>

            <!-- 4 Metric Cards (2x2 Grid) with 20px numbers -->
            <div class="grid grid-cols-2 gap-2">
              <!-- 1. Peak Position -->
              <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ song.bestPosition?.rank ? '#' + song.bestPosition.rank : '-' }}
                </p>
                <p class="text-xs text-text-half-dimmed font-normal">Peak Position</p>
              </div>

              <!-- 2. Weeks Charted -->
              <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ song.postitions?.length || 0 }}
                </p>
                <p class="text-xs text-text-half-dimmed font-normal">Weeks Charted</p>
              </div>

              <!-- 3. Total Streams -->
              <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ formatStreams(song.streams) }}
                </p>
                <p class="text-xs text-text-half-dimmed font-normal">Total Streams</p>
              </div>

              <!-- 4. Duration -->
              <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ formatDuration(song.durationMs) }}
                </p>
                <p class="text-xs text-text-half-dimmed font-normal">Duration</p>
              </div>
            </div>

            <!-- Weekly Chart History (Trend Chart) -->
            <div class="space-y-1.5">
              <h4 class="text-xs font-medium text-text-dimmed">Weekly Chart History</h4>
              <div class="relative rounded-md border border-card-border-border bg-card-bg-default p-3">
                @if (songHistoryPoints().length > 0) {
                  <div class="relative w-full">
                    <svg 
                      viewBox="0 0 520 180" 
                      class="h-36 w-full cursor-crosshair overflow-visible"
                      (mousemove)="onChartMouseMove($event)"
                      (mouseleave)="onChartMouseLeave()"
                    >
                      <defs>
                        <linearGradient id="songAreaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#818cf8" stop-opacity="0.3" />
                          <stop offset="100%" stop-color="#818cf8" stop-opacity="0" />
                        </linearGradient>
                      </defs>
                      <polyline [attr.points]="songTrendPoints() + ' 520,180 0,180'" fill="url(#songAreaGradient)" stroke="none"></polyline>
                      <polyline [attr.points]="songTrendPoints()" fill="none" stroke="#818cf8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></polyline>

                      <!-- Interactive Guide Line and circles -->
                      @if (hoveredPoint(); as hp) {
                        <line
                          [attr.x1]="hp.x"
                          [attr.y1]="0"
                          [attr.x2]="hp.x"
                          [attr.y2]="180"
                          stroke="#71717a"
                          stroke-width="1.5"
                          stroke-dasharray="4,4"
                          class="pointer-events-none"
                        ></line>

                        <!-- Circle on path -->
                        <circle
                          [attr.cx]="hp.x"
                          [attr.cy]="hp.y"
                          r="5"
                          fill="#818cf8"
                          stroke="#18181b"
                          stroke-width="2"
                          class="pointer-events-none"
                        ></circle>
                      }
                    </svg>

                    <!-- Floating tooltip card -->
                    @if (hoveredPoint(); as hp) {
                      <div 
                        class="pointer-events-none absolute z-50 rounded-lg border border-card-border-border bg-card-bg-default p-2 text-xs shadow-xl transition-all duration-75"
                        [style.left.%]="(hp.x / 520) * 100"
                        [style.transform]="hp.x > 360 ? 'translate(-105%, -115%)' : 'translate(5%, -115%)'"
                        [style.top.px]="(hp.y / 180) * 140"
                      >
                        <p class="font-bold text-text-primary text-sm">#{{ hp.rank }}</p>
                        <p class="text-[10px] text-text-dimmed mt-0.5">{{ hp.date }}</p>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-xs text-text-dimmed py-2 text-center">No chart history available for this song.</p>
                }
              </div>
            </div>

            <!-- Awards Section -->
            @if (song.awards && song.awards.length > 0) {
              <div class="space-y-1.5">
                <h4 class="text-xs font-medium text-text-dimmed">Awards & Certifications</h4>
                <div class="flex flex-wrap gap-1.5">
                  @for (aw of song.awards; track aw.award) {
                    <app-badge
                      variant="neutral"
                      size="tiny"
                      [label]="getAwardLabel(aw)"
                    />
                  }
                </div>
              </div>
            }

            <!-- Metadata info -->
            <div class="space-y-3 pb-2">
              <div>
                <p class="text-xs font-medium text-text-dimmed mb-0.5">Record Label / Company</p>
                <p class="text-xs font-semibold text-text-primary">{{ song.company || 'Independent / Unknown' }}</p>
              </div>
              @if (song.isrcs && song.isrcs.length > 0) {
                <div>
                  <p class="text-xs font-medium text-text-dimmed mb-0.5">
                    ISRC Code{{ song.isrcs.length > 1 ? 's' : '' }}
                  </p>
                  <p class="text-xs font-semibold text-text-primary">{{ song.isrcs.join(', ') }}</p>
                </div>
              }
              <div>
                <p class="text-xs font-medium text-text-dimmed mb-0.5">Duration</p>
                <p class="text-xs font-semibold text-text-primary">{{ formatDuration(song.durationMs) }}</p>
              </div>
            </div>
          } @else {
            <p class="text-xs text-text-dimmed py-4 text-center">
              Song details could not be loaded.
            </p>
          }

        </div>
      </div>

      <!-- Footer Button: Play on Spotify (12px padding) -->
      @if (songDetails()?.spotifyId) {
        <div class="p-3 pt-2 shrink-0 bg-side-drawer-bg">
          <app-button
            variant="primary"
            size="large"
            class="w-full block"
            className="w-full justify-center"
            label="Play on Spotify"
            (btnClick)="playOnSpotify()"
          />
        </div>
      }

    </div>
  }
</aside>

